import React, { useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Download } from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";

export default function GraficoDatos({
    titulosColumnas,
    titulosColumnas2,
    datosTabla,
    tipoGrafico = "bar",
    tituloX,
    tituloY,
    ajustarRangoY = false,
    nombreArchivo,
}) {
    const contenedorRef = useRef(null);

    if (!datosTabla || datosTabla.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center", color: "#757575" }}>
                <Typography>No hay datos para mostrar</Typography>
            </Box>
        );
    }

    // Verificar si hay dos filas de encabezado
    const tieneDobleEncabezado = titulosColumnas2 && titulosColumnas2.length > 0;

    // Preparar datos para el gráfico
    const labels = datosTabla.map((fila) => fila[0]?.toString() || "");

    // En las tablas de doble encabezado (RVR y Visibilidad/Altura de Nubes)
    // las filas son horas: el eje X muestra la hora y los nombres de las
    // variables van en las etiquetas de las series
    let ejeXTitulo = tituloX || titulosColumnas[0] || "";
    let nombreSerie1 = "";
    let nombreSerie2 = "";
    if (tieneDobleEncabezado && titulosColumnas2[0]) {
        const titulo1 = titulosColumnas[0]?.replace(/\s*\(.*\)/, "") || "";
        nombreSerie1 = /VISIBILIDAD/i.test(titulo1) ? "Vis." : titulo1;
        const titulo2Match = titulosColumnas2[0]?.match(/ALT\.?\s*NUBE[S]?/i);
        nombreSerie2 = titulo2Match ? "Nubes" : titulosColumnas2[0]?.replace(/HORA\s*\(UTC\)\s*\/?\s*/i, "").replace(/\s*\(.*\)/, "") || "";
        ejeXTitulo = "Hora (UTC)";
    }

    // El título del eje Y se infiere o usa "Valor"
    const ejeYTitulo = tituloY || "Valor";

    // Obtener las series de datos (columnas excepto la primera que son los labels)
    const series = [];
    const numColumnas = titulosColumnas.length;

    for (let col = 1; col < numColumnas; col++) {
        const data = datosTabla.map((fila) => {
            const valor = fila[col];
            if (valor === undefined || valor === null || valor === "") return 0;
            // Convertir string con coma a número
            const numStr = valor.toString().replace(",", ".");
            return parseFloat(numStr) || 0;
        });

        // Combinar etiquetas de ambos encabezados si existen, indicando a qué
        // variable corresponde cada rango
        let seriesLabel = titulosColumnas[col] || `Serie ${col}`;
        if (tieneDobleEncabezado) {
            seriesLabel = `${nombreSerie1} ${titulosColumnas[col]}`.trim();
            if (titulosColumnas2[col]) {
                seriesLabel += ` / ${nombreSerie2} ${titulosColumnas2[col]}`;
            }
        }

        series.push({
            data,
            label: seriesLabel,
        });
    }

    // Colores para las series (extendido para soportar más intervalos)
    const colors = [
        "#1a8a9e",
        "#3baec7",
        "#7cb342",
        "#ffa726",
        "#ef5350",
        "#ab47bc",
        "#26a69a",
        "#ec407a",
        "#5c6bc0",
        "#66bb6a",
        "#ffca28",
        "#8d6e63",
        "#42a5f5",
        "#78909c",
        "#d4e157",
        "#ff7043",
    ];

    const chartHeight = 400;

    // Mostrar todas las series de datos sin límite
    const seriesToShow = series;

    // Calcular rango Y ajustado si es necesario
    let yAxisConfig = {
        label: ejeYTitulo,
        labelStyle: {
            fontSize: 12,
            fontWeight: 600,
            fill: "#424242",
        },
    };

    if (ajustarRangoY) {
        // Obtener todos los valores de todas las series
        const allValues = seriesToShow.flatMap((s) => s.data).filter((v) => v !== 0 && v !== null);
        // Solo ajustar si hay al menos dos valores distintos: con min === max
        // el eje queda degenerado y el gráfico no se dibuja
        if (allValues.length > 0 && Math.min(...allValues) < Math.max(...allValues)) {
            const minVal = Math.min(...allValues);
            const maxVal = Math.max(...allValues);
            // Agregar un margen del 5% arriba y abajo
            const margin = (maxVal - minVal) * 0.05;
            yAxisConfig.min = Math.floor(minVal - margin);
            yAxisConfig.max = Math.ceil(maxVal + margin);
        }
    }

    // Exporta el gráfico como PNG: clona el SVG copiando los estilos
    // calculados (en MUI X Charts viven en clases CSS y se perderían al
    // serializar), lo dibuja en un canvas a 2x y descarga el resultado
    const handleDescargarImagen = () => {
        const svg = contenedorRef.current?.querySelector("svg");
        if (!svg) return;

        const clon = svg.cloneNode(true);
        const origEls = [svg, ...svg.querySelectorAll("*")];
        const clonEls = [clon, ...clon.querySelectorAll("*")];
        const propiedades = [
            "fill", "fill-opacity", "stroke", "stroke-width", "stroke-opacity",
            "stroke-dasharray", "font-family", "font-size", "font-weight",
            "opacity", "text-anchor", "dominant-baseline",
        ];
        origEls.forEach((el, i) => {
            const computados = window.getComputedStyle(el);
            const estilo = propiedades
                .map((p) => `${p}:${computados.getPropertyValue(p)}`)
                .join(";");
            clonEls[i].setAttribute("style", estilo);
        });

        const { width, height } = svg.getBoundingClientRect();
        clon.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clon.setAttribute("width", width);
        clon.setAttribute("height", height);

        const escala = 2;
        const imagen = new Image();
        imagen.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = width * escala;
            canvas.height = height * escala;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(escala, escala);
            ctx.drawImage(imagen, 0, 0);
            const enlace = document.createElement("a");
            enlace.download = `${(nombreArchivo || "grafico").replace(/[^\wáéíóúñÁÉÍÓÚÑ]+/g, "_")}.png`;
            enlace.href = canvas.toDataURL("image/png");
            enlace.click();
        };
        imagen.src =
            "data:image/svg+xml;charset=utf-8," +
            encodeURIComponent(new XMLSerializer().serializeToString(clon));
    };

    const botonDescarga = (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
                size="small"
                startIcon={<Download />}
                onClick={handleDescargarImagen}
                sx={{
                    color: "#1a8a9e",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#e0f7fa" },
                }}
            >
                Guardar imagen
            </Button>
        </Box>
    );

    if (tipoGrafico === "line") {
        return (
            <Box>
                {botonDescarga}
                <Box ref={contenedorRef} sx={{ width: "100%", height: chartHeight }}>
                    <LineChart
                        xAxis={[
                            {
                                data: labels,
                                scaleType: "band",
                                label: ejeXTitulo,
                                tickLabelStyle: {
                                    fontSize: 11,
                                },
                                labelStyle: {
                                    fontSize: 12,
                                    fontWeight: 600,
                                    fill: "#424242",
                                },
                            },
                        ]}
                        yAxis={[yAxisConfig]}
                        series={seriesToShow.map((s, i) => ({
                            ...s,
                            color: colors[i % colors.length],
                            curve: "linear",
                        }))}
                        height={chartHeight}
                        margin={{ left: 70, right: 20, top: 40, bottom: 60 }}
                        slotProps={{
                            legend: {
                                direction: "row",
                                position: { vertical: "top", horizontal: "middle" },
                                padding: 0,
                                itemMarkWidth: 10,
                                itemMarkHeight: 10,
                                markGap: 5,
                                itemGap: 15,
                                labelStyle: {
                                    fontSize: 11,
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            {botonDescarga}
            <Box ref={contenedorRef} sx={{ width: "100%", height: chartHeight }}>
                <BarChart
                    xAxis={[
                        {
                            data: labels,
                            scaleType: "band",
                            label: ejeXTitulo,
                            tickLabelStyle: {
                                fontSize: 11,
                                angle: labels.length > 12 ? 45 : 0,
                                textAnchor: labels.length > 12 ? "start" : "middle",
                            },
                            labelStyle: {
                                fontSize: 12,
                                fontWeight: 600,
                                fill: "#424242",
                            },
                        },
                    ]}
                    yAxis={[yAxisConfig]}
                    series={seriesToShow.map((s, i) => ({
                        ...s,
                        color: colors[i % colors.length],
                    }))}
                    height={chartHeight}
                    margin={{
                        left: 70,
                        right: 20,
                        top: 40,
                        bottom: labels.length > 12 ? 90 : 60
                    }}
                    slotProps={{
                        legend: {
                            direction: "row",
                            position: { vertical: "top", horizontal: "middle" },
                            padding: 0,
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                            markGap: 5,
                            itemGap: 15,
                            labelStyle: {
                                fontSize: 11,
                            },
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
